import { useMemo } from 'react';
import { Checkbox } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getTableCheckBox,
  handleTableCheckBoxChange,
  handleTableCheckBoxChangeAll,
} from 'redux/BuilderModeSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

interface TableCheckBoxProperties {
  isHeading: boolean;
  itemId: number;
}

/**
 * Revamp copy of `utils/ReactGridUtils`'s TableCheckBox.
 *
 * The purple styling lives here as inline `sx` rather than in the global
 * checkbox theme's `'delete'` variant. That variant is shared with the
 * original Dimensions table, so restyling it there would have leaked the
 * revamp's look into the original — this keeps the two fully independent.
 * Redux wiring is identical to the original.
 */
const checkboxSx = {
  '.chakra-checkbox__control': {
    borderWidth: '1px',
    borderColor: '#D1D6DD',
    borderRadius: '4px',
    background: 'white',
    height: '16px',
    width: '16px',
    _focus: { boxShadow: 'none' },
  },
  '.chakra-checkbox__control[data-checked], .chakra-checkbox__control[data-indeterminate]': {
    background: '#8B5CF6',
    borderColor: '#8B5CF6',
    color: 'white',
  },
  '.chakra-checkbox__control[data-checked]:hover': {
    background: '#8B5CF6',
    borderColor: '#8B5CF6',
  },
  '.chakra-checkbox__icon': {
    width: '10px',
    height: '10px',
  },
};

export const TableCheckBox = ({ isHeading, itemId }: TableCheckBoxProperties) => {
  const dispatch: AppDispatch = useDispatch();
  const tableCheckBox: any = useSelector(getTableCheckBox);

  const allChecked = useMemo(() => {
    return tableCheckBox?.[tableCheckBox?.length && tableCheckBox.length - 2]?.status;
  }, [tableCheckBox?.[tableCheckBox?.length && tableCheckBox.length - 2]?.status]);

  const isIndeterminate = useMemo(() => {
    return tableCheckBox?.[tableCheckBox?.length && tableCheckBox.length - 1]?.status;
  }, [tableCheckBox?.[tableCheckBox?.length && tableCheckBox.length - 1]?.status]);

  const desiredObject = useMemo(
    () => tableCheckBox.find((object: any) => object.id === itemId),
    [tableCheckBox, itemId],
  );

  return (
    <>
      {isHeading ? (
        <Checkbox
          className='delete-all-checkbox'
          sx={checkboxSx}
          isChecked={Boolean(allChecked)}
          isIndeterminate={Boolean(isIndeterminate)}
          onChange={(event) => {
            dispatch(handleTableCheckBoxChangeAll({ status: event.target.checked }));
          }}
        ></Checkbox>
      ) : (
        <Checkbox
          sx={checkboxSx}
          isChecked={Boolean(desiredObject?.status)}
          onChange={(event) => {
            dispatch(handleTableCheckBoxChange({ itemId, status: event.target.checked }));
          }}
        ></Checkbox>
      )}
    </>
  );
};

export default TableCheckBox;
